import { useRef, useState } from "react";
import {
  diffLines,
  diffStats,
  type DiffLine,
  type MockApiOptions,
  type RepairOutcome,
  type RepairStep,
  type ScriptAssertion,
  type ScriptLanguage,
} from "@roqeet/core";
import { runScriptTest, type ScriptTestResult } from "../../state/script-test.js";
import {
  aiRepairWithTest,
  quickRepairWithTest,
  type QuickRepairOutcome,
} from "../../state/repair-actions.js";
import { useT } from "../../i18n/use-t.js";

/**
 * 스크립트 테스트 패널 — 결정 #30(테스트 환경, UI). 편집 중인 스크립트를 실제 대화
 * 없이 모킹 환경에서 실행해 입력→출력·변수 변화·로그·오류를 즉시 확인한다. 실행은
 * state/script-test의 러너가 실제 샌드박스(QuickJS/wasmoon)로 처리한다.
 *
 * 변수 시드는 chat 스코프 몇 개만 폼으로 받는다(가장 흔한 케이스). 값은 숫자로
 * 보이면 숫자로, 아니면 문자열로 해석한다(게임/봇 변수 관례).
 */

interface VarRow {
  key: string;
  value: string;
}

/** 기대 변수 한 행(어서션). scope는 chat 고정(폼 단순화 — 가장 흔함). */
interface ExpectVarRow {
  key: string;
  value: string;
}

/** "10" → 10, "true"/"false" → boolean, 그 외 문자열. */
function coerce(v: string): number | string | boolean {
  if (v === "true") return true;
  if (v === "false") return false;
  if (v.trim() !== "" && Number.isFinite(Number(v))) return Number(v);
  return v;
}

export function ScriptTestPanel({
  language,
  source,
  onApply,
}: {
  language: ScriptLanguage;
  source: string;
  /** 설정 시 테스트 실패에 따른 AI 자동 수리를 노출한다(수리 결과를 에디터에 적용). */
  onApply?: (next: string) => void;
}) {
  const { t } = useT();
  const [inputText, setInputText] = useState(t("scriptTest.defaultInput"));
  const [vars, setVars] = useState<VarRow[]>([]);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<ScriptTestResult | null>(null);

  // AI 자동 수리 상태(테스트 기반). 테스트가 실패했을 때만 버튼이 뜬다(토큰 보호).
  const [repairBusy, setRepairBusy] = useState(false);
  const [repairSteps, setRepairSteps] = useState<RepairStep[]>([]);
  const [repairOutcome, setRepairOutcome] = useState<RepairOutcome | null>(null);
  const [repairError, setRepairError] = useState<string | null>(null);
  const repairAbort = useRef<AbortController | null>(null);

  // 빠른 수정(AI 0) 상태: 에러 메시지 기반 결정적 오타 교정.
  const [quickBusy, setQuickBusy] = useState(false);
  const [quickOutcome, setQuickOutcome] = useState<QuickRepairOutcome | null>(null);

  // 어서션(선택): 켜면 "기대값 통과/실패"까지 평가한다(결정 #30).
  const [assertOn, setAssertOn] = useState(false);
  const [expectMode, setExpectMode] = useState<"none" | "equals" | "contains" | "not-contains">(
    "none",
  );
  const [expectText, setExpectText] = useState("");
  const [expectVars, setExpectVars] = useState<ExpectVarRow[]>([]);

  /** 폼 → ScriptAssertion. 아무 기대도 없으면 undefined(어서션 미적용). */
  const buildAssertion = (): ScriptAssertion | undefined => {
    if (!assertOn) return undefined;
    const a: ScriptAssertion = {};
    if (expectMode === "equals" && expectText) a.expectText = expectText;
    if (expectMode === "contains" && expectText) a.expectTextContains = expectText;
    if (expectMode === "not-contains" && expectText) a.expectTextNotContains = expectText;
    const evs = expectVars
      .filter((r) => r.key.trim())
      .map((r) => ({ scope: "chat" as const, key: r.key.trim(), value: coerce(r.value) }));
    if (evs.length) a.expectVars = evs;
    return Object.keys(a).length ? a : undefined;
  };

  /** 폼 → 모킹 옵션(입력 변수 시드). run과 자동 수리가 공유. */
  const buildMock = (): MockApiOptions => {
    const chat: Record<string, unknown> = {};
    for (const r of vars) {
      if (r.key.trim()) chat[r.key.trim()] = coerce(r.value);
    }
    return Object.keys(chat).length ? { vars: { chat } } : {};
  };

  const run = async () => {
    setBusy(true);
    setResult(null);
    setRepairOutcome(null);
    setRepairSteps([]);
    setRepairError(null);
    try {
      const assertion = buildAssertion();
      const res = await runScriptTest({
        language,
        source,
        inputText,
        mock: buildMock(),
        ...(assertion ? { assertion } : {}),
      });
      setResult(res);
    } catch (e) {
      setResult({
        ok: false,
        outputText: "",
        snapshot: { vars: { chat: {}, character: {}, global: {} }, logs: [], storage: {} },
        logs: [],
        error: e instanceof Error ? e.message : String(e),
      });
    } finally {
      setBusy(false);
    }
  };

  /** 테스트가 실패했는지(실행 오류 또는 어서션 실패). 자동 수리 노출 조건. */
  const testFailed = !!result && (!result.ok || (!!result.assertion && !result.assertion.pass));

  /** 테스트 실행 결과를 검증기로 삼아 AI가 통과할 때까지 고친다(결정 #30). */
  const autoRepair = async () => {
    if (language !== "javascript" && language !== "lua") return;
    setRepairBusy(true);
    setRepairError(null);
    setRepairSteps([]);
    setRepairOutcome(null);
    const ctrl = new AbortController();
    repairAbort.current = ctrl;
    try {
      const assertion = buildAssertion();
      const outcome = await aiRepairWithTest({
        language,
        source,
        inputText,
        mock: buildMock(),
        ...(assertion ? { assertion } : {}),
        signal: ctrl.signal,
        onStep: (s) => setRepairSteps((prev) => [...prev, s]),
      });
      setRepairOutcome(outcome);
    } catch (e) {
      setRepairError(e instanceof Error ? e.message : String(e));
    } finally {
      setRepairBusy(false);
      repairAbort.current = null;
    }
  };

  /** 빠른 수정(AI 0): 에러 메시지 기반 결정적 오타 교정 + 재실행. 토큰을 쓰지 않는다. */
  const quickRepair = async () => {
    if (language !== "javascript" && language !== "lua") return;
    setQuickBusy(true);
    setQuickOutcome(null);
    try {
      const assertion = buildAssertion();
      const outcome = await quickRepairWithTest({
        language,
        source,
        inputText,
        mock: buildMock(),
        ...(assertion ? { assertion } : {}),
      });
      setQuickOutcome(outcome);
    } finally {
      setQuickBusy(false);
    }
  };

  const repairChanged = repairOutcome && repairOutcome.source !== source;
  const repairDiff: DiffLine[] | null = repairChanged ? diffLines(source, repairOutcome!.source) : null;
  const repairDiffStats = repairDiff ? diffStats(repairDiff) : null;

  return (
    <div className="space-y-2 rounded-lg border border-line bg-bg-raised/40 p-2">
      <div className="text-xs font-semibold text-ink-faint">{t("scriptTest.title")}</div>

      <div>
        <span className="mb-1 block text-xs text-ink-soft">{t("scriptTest.inputText")}</span>
        <textarea
          className="w-full resize-none rounded-lg border border-line bg-bg-soft px-2 py-1.5 text-ink outline-none focus:border-accent"
          style={{ fontSize: 16, minHeight: 48 }}
          value={inputText}
          onChange={(e) => setInputText(e.target.value)}
        />
      </div>

      <div>
        <span className="mb-1 block text-xs text-ink-soft">{t("scriptTest.varSeed")}</span>
        <div className="space-y-1">
          {vars.map((row, i) => (
            <div key={i} className="flex items-center gap-2">
              <input
                className="min-w-0 flex-1 rounded border border-line bg-bg-soft px-2 py-1 text-sm text-ink outline-none focus:border-accent"
                placeholder={t("scriptTest.phKeyHp")}
                value={row.key}
                onChange={(e) =>
                  setVars(vars.map((r, j) => (j === i ? { ...r, key: e.target.value } : r)))
                }
              />
              <input
                className="min-w-0 flex-1 rounded border border-line bg-bg-soft px-2 py-1 text-sm text-ink outline-none focus:border-accent"
                placeholder={t("scriptTest.phValue")}
                value={row.value}
                onChange={(e) =>
                  setVars(vars.map((r, j) => (j === i ? { ...r, value: e.target.value } : r)))
                }
              />
              <button
                className="shrink-0 rounded px-2 py-1 text-xs text-red-400 hover:bg-bg-raised"
                onClick={() => setVars(vars.filter((_, j) => j !== i))}
                aria-label={t("scriptTest.deleteVar")}
              >
                ✕
              </button>
            </div>
          ))}
          <button
            className="rounded-lg border border-dashed border-line px-2 py-1 text-xs text-ink-soft hover:border-accent hover:text-ink"
            onClick={() => setVars([...vars, { key: "", value: "" }])}
          >
            {t("scriptTest.addVar")}
          </button>
        </div>
      </div>

      <button
        className="rounded-lg bg-accent px-3 py-1.5 text-sm font-medium text-white hover:bg-accent-soft disabled:opacity-50"
        onClick={() => void run()}
        disabled={busy || !source.trim()}
      >
        {busy ? t("scriptTest.running") : t("scriptTest.run")}
      </button>

      {/* 어서션(선택): 기대값 통과/실패 검사(결정 #30). */}
      <div className="rounded-lg border border-line bg-bg-soft p-2">
        <label className="flex items-center gap-2 text-xs text-ink-soft">
          <input type="checkbox" checked={assertOn} onChange={(e) => setAssertOn(e.target.checked)} />
          {t("scriptTest.useAssertion")}
        </label>

        {assertOn && (
          <div className="mt-2 space-y-2">
            <div>
              <span className="mb-1 block text-xs text-ink-faint">{t("scriptTest.outputText")}</span>
              <div className="flex gap-2">
                <select
                  className="rounded border border-line bg-bg-raised px-2 py-1 text-sm text-ink outline-none focus:border-accent"
                  value={expectMode}
                  onChange={(e) => setExpectMode(e.target.value as typeof expectMode)}
                >
                  <option value="none">{t("scriptTest.modeNone")}</option>
                  <option value="equals">{t("scriptTest.modeEquals")}</option>
                  <option value="contains">{t("scriptTest.modeContains")}</option>
                  <option value="not-contains">{t("scriptTest.modeNotContains")}</option>
                </select>
                {expectMode !== "none" && (
                  <input
                    className="min-w-0 flex-1 rounded border border-line bg-bg-raised px-2 py-1 text-sm text-ink outline-none focus:border-accent"
                    placeholder={t("scriptTest.phExpectedText")}
                    value={expectText}
                    onChange={(e) => setExpectText(e.target.value)}
                  />
                )}
              </div>
            </div>

            <div>
              <span className="mb-1 block text-xs text-ink-faint">{t("scriptTest.expectedVars")}</span>
              <div className="space-y-1">
                {expectVars.map((row, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <input
                      className="min-w-0 flex-1 rounded border border-line bg-bg-raised px-2 py-1 text-sm text-ink outline-none focus:border-accent"
                      placeholder={t("scriptTest.phKeyHp")}
                      value={row.key}
                      onChange={(e) =>
                        setExpectVars(
                          expectVars.map((r, j) => (j === i ? { ...r, key: e.target.value } : r)),
                        )
                      }
                    />
                    <input
                      className="min-w-0 flex-1 rounded border border-line bg-bg-raised px-2 py-1 text-sm text-ink outline-none focus:border-accent"
                      placeholder={t("scriptTest.phExpectedValue")}
                      value={row.value}
                      onChange={(e) =>
                        setExpectVars(
                          expectVars.map((r, j) => (j === i ? { ...r, value: e.target.value } : r)),
                        )
                      }
                    />
                    <button
                      className="shrink-0 rounded px-2 py-1 text-xs text-red-400 hover:bg-bg-raised"
                      onClick={() => setExpectVars(expectVars.filter((_, j) => j !== i))}
                      aria-label={t("scriptTest.deleteExpectedVar")}
                    >
                      ✕
                    </button>
                  </div>
                ))}
                <button
                  className="rounded-lg border border-dashed border-line px-2 py-1 text-xs text-ink-soft hover:border-accent hover:text-ink"
                  onClick={() => setExpectVars([...expectVars, { key: "", value: "" }])}
                >
                  {t("scriptTest.addExpectedVar")}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {result && (
        <div className="space-y-2 rounded-lg border border-line bg-bg-soft p-2 text-xs">
          <div className={result.ok ? "text-green-300" : "text-red-300"}>
            {result.ok ? t("scriptTest.runOk") : t("scriptTest.runError")}
          </div>

          {/* 어서션 결과(있을 때): 통과/실패 + 실패 사유. */}
          {result.assertion && (
            <div
              className={[
                "rounded p-2",
                result.assertion.pass
                  ? "bg-green-500/10 text-green-300"
                  : "bg-red-500/10 text-red-300",
              ].join(" ")}
            >
              <div className="font-semibold">
                {result.assertion.pass ? t("scriptTest.assertOk") : t("scriptTest.assertFail")}
              </div>
              {result.assertion.failures.length > 0 && (
                <ul className="mt-1 space-y-1">
                  {result.assertion.failures.map((f, i) => (
                    <li key={i} className="whitespace-pre-wrap break-words">
                      · {f}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {result.error && (
            <pre className="whitespace-pre-wrap break-words rounded bg-red-950/40 p-2 text-red-300">
              {result.error}
            </pre>
          )}

          <div>
            <span className="text-ink-faint">{t("scriptTest.outputText")}</span>
            <pre className="mt-0.5 max-h-32 overflow-auto whitespace-pre-wrap break-words rounded bg-bg-raised p-2 text-ink">
              {result.outputText || t("scriptTest.emptyString")}
            </pre>
          </div>

          {Object.keys(result.snapshot.vars.chat).length > 0 && (
            <div>
              <span className="text-ink-faint">{t("scriptTest.finalVars")}</span>
              <pre className="mt-0.5 max-h-32 overflow-auto rounded bg-bg-raised p-2 text-ink">
                {JSON.stringify(result.snapshot.vars.chat, null, 2)}
              </pre>
            </div>
          )}

          {result.logs.length > 0 && (
            <div>
              <span className="text-ink-faint">
                {t("scriptTest.logs", { count: result.logs.length })}
              </span>
              <pre className="mt-0.5 max-h-32 overflow-auto whitespace-pre-wrap break-words rounded bg-bg-raised p-2 text-ink-soft">
                {result.logs.map((l) => l.map((x) => String(x)).join(" ")).join("\n")}
              </pre>
            </div>
          )}

          {/* 테스트 실패 시 빠른 수정(AI 0). 에러 메시지 기반 결정적 오타 교정 — 먼저 권한다. */}
          {onApply && testFailed && (
            <div className="space-y-2 rounded-lg border border-line bg-bg-raised/40 p-2">
              <div className="flex items-center justify-between gap-2">
                <span className="text-xs font-semibold text-ink">{t("scriptTest.quickTitle")}</span>
                <button
                  className="rounded-md border border-line px-2 py-0.5 text-xs text-ink-soft hover:bg-bg-raised disabled:opacity-50"
                  onClick={() => void quickRepair()}
                  disabled={quickBusy}
                >
                  {quickBusy ? t("scriptTest.fixing") : t("scriptTest.quickFix")}
                </button>
              </div>
              <p className="text-[11px] text-ink-faint">
                {t("scriptTest.quickHint")}
              </p>

              {quickOutcome && (
                <div className="space-y-1">
                  {quickOutcome.applied.length > 0 ? (
                    <ul className="space-y-0.5 text-[11px] text-ink-soft">
                      {quickOutcome.applied.map((a, i) => (
                        <li key={i}>· {a}</li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-[11px] text-ink-faint">
                      {t("scriptTest.quickNone")}
                    </p>
                  )}
                  <div
                    className={[
                      "rounded px-2 py-1 text-xs",
                      quickOutcome.ok ? "bg-green-500/10 text-green-300" : "bg-amber-500/10 text-amber-300",
                    ].join(" ")}
                  >
                    {quickOutcome.ok
                      ? t("scriptTest.quickOk")
                      : quickOutcome.changed
                        ? t("scriptTest.quickPartial")
                        : t("scriptTest.quickFailed")}
                  </div>
                  {quickOutcome.changed && (
                    <button
                      className="rounded-lg bg-accent px-3 py-1 text-xs font-medium text-white hover:bg-accent-soft"
                      onClick={() => {
                        onApply(quickOutcome.source);
                        setQuickOutcome(null);
                        setResult(null);
                      }}
                    >
                      {t("common.apply")}
                    </button>
                  )}
                </div>
              )}
            </div>
          )}

          {/* 테스트 실패 시 AI 자동 수리(옵트인). onApply가 있을 때만 노출. */}
          {onApply && testFailed && (
            <div className="space-y-2 rounded-lg border border-accent/40 bg-accent/5 p-2">
              <div className="flex items-center justify-between gap-2">
                <span className="text-xs font-semibold text-accent-soft">
                  {t("scriptTest.aiTitle")}
                </span>
                {repairBusy ? (
                  <button
                    className="rounded-md border border-line px-2 py-0.5 text-xs text-red-300 hover:bg-bg-raised"
                    onClick={() => repairAbort.current?.abort()}
                  >
                    {t("scriptTest.stop")}
                  </button>
                ) : (
                  <button
                    className="rounded-md bg-accent px-2 py-0.5 text-xs font-medium text-white hover:bg-accent-soft"
                    onClick={() => void autoRepair()}
                  >
                    {t("scriptTest.aiFix")}
                  </button>
                )}
              </div>
              <p className="text-[11px] text-ink-faint">
                {t("scriptTest.aiHint")}
              </p>

              {repairError && <p className="text-xs text-red-300">{repairError}</p>}

              {repairSteps.length > 0 && (
                <ul className="space-y-0.5 text-[11px] text-ink-soft">
                  {repairSteps.map((s, i) => (
                    <li key={i}>
                      {repairStepLabel(s, t)}
                      {s.errors.length > 0 && (
                        <span className="text-amber-300">
                          {" · "}{t("scriptTest.errorCount", { count: s.errors.length })}
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
              )}

              {repairOutcome && (
                <div className="space-y-2">
                  <div
                    className={[
                      "rounded px-2 py-1 text-xs",
                      repairOutcome.ok ? "bg-green-500/10 text-green-300" : "bg-amber-500/10 text-amber-300",
                    ].join(" ")}
                  >
                    {repairOutcome.ok
                      ? t("scriptTest.repairOk", { count: repairOutcome.iterations })
                      : repairOutcome.stoppedReason === "no-progress"
                        ? t("scriptTest.repairNoProgress")
                        : repairOutcome.stoppedReason === "max-iterations"
                          ? t("scriptTest.repairMax", { count: repairOutcome.iterations })
                          : repairOutcome.stoppedReason === "aborted"
                            ? t("scriptTest.stopped")
                            : t("scriptTest.repairFailed")}
                  </div>

                  {!repairChanged ? (
                    <p className="text-[11px] text-ink-faint">{t("misc.noChanges")}</p>
                  ) : (
                    <>
                      <div className="text-[11px] font-semibold text-ink-faint">
                        {t("misc.diffPreview")}{" "}
                        <span className="text-green-400">+{repairDiffStats!.added}</span>{" "}
                        <span className="text-red-400">-{repairDiffStats!.removed}</span>
                      </div>
                      <pre className="max-h-56 overflow-auto rounded border border-line bg-bg-soft p-2 font-mono text-[11px] leading-relaxed">
                        {repairDiff!.map((d, i) => (
                          <div
                            key={i}
                            className={
                              d.op === "add"
                                ? "bg-green-500/10 text-green-300"
                                : d.op === "remove"
                                  ? "bg-red-500/10 text-red-300"
                                  : "text-ink-soft"
                            }
                          >
                            <span className="select-none text-ink-faint">
                              {d.op === "add" ? "+ " : d.op === "remove" ? "- " : "  "}
                            </span>
                            {d.text || "\u00a0"}
                          </div>
                        ))}
                      </pre>
                      <button
                        className="rounded-lg bg-accent px-3 py-1 text-xs font-medium text-white hover:bg-accent-soft"
                        onClick={() => {
                          onApply(repairOutcome.source);
                          setRepairOutcome(null);
                          setResult(null);
                        }}
                      >
                        {t("common.apply")}
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/** 수리 진행 단계 라벨(타임라인). */
type Translate = ReturnType<typeof useT>["t"];

function repairStepLabel(s: RepairStep, t: Translate): string {
  const it = s.iteration > 0 ? `#${s.iteration} ` : "";
  switch (s.phase) {
    case "pre-pass":
      return `${it}${t("scriptTest.phasePrePass")}`;
    case "validate":
      return `${it}${t("scriptTest.phaseValidate")}`;
    case "ai":
      return `${it}${t("scriptTest.phaseAi")}`;
    case "aborted":
      return `${it}${t("scriptTest.phaseAborted")}`;
    case "error":
      return `${it}${t("scriptTest.phaseError")}`;
    default:
      return `${it}${s.phase}`;
  }
}
